import React, { type ComponentType, useState, useCallback, useRef } from 'react';
import { graphql, useFragment } from 'react-relay';
import { useEntryPoint } from '@atlassian/jira-entry-point';
import { useEntryPointButtonTrigger } from '@atlassian/jira-entry-point-button-trigger';
import { ff } from '@atlassian/jira-feature-flagging';
import useMergeRefs from '@atlassian/jira-merge-refs';
import type { footerRenderer_nativeIssueTable$key as IssuesKey } from '@atlassian/jira-relay/src/__generated__/footerRenderer_nativeIssueTable.graphql';
import { useCloudId } from '@atlassian/jira-tenant-context-controller';
import type { IssueCountProps, FooterProps } from '../../common/types';
import { Footer as DefaultFooter } from '../../common/ui/components/footer';
import ContextFieldsEntryPoint from '../../common/ui/issue-create-form/context-fields/entrypoint';
import IssueCountRenderer from './issue-count-renderer';
import { IssueCreateButton } from './issue-create-button';
import { IssueCreateFooter } from './issue-create-footer';
import { PaginationControls } from './pagination-controls';

type Props = {
	/**
	 * Overridable Footer component to render.
	 */
	Footer?: ComponentType<FooterProps>;
	/**
	 * Overridable IssueCount component to render.
	 */
	IssueCount?: ComponentType<IssueCountProps>;
	/**
	 * Relay fragment key for a connection of issues.
	 */
	issues: IssuesKey;
	/**
	 * Key of the project where new issues should be created. When defined this will enable the issue create button in
	 * the table footer.
	 */
	issueCreateProjectKey?: string;
	/**
	 * Callback invoked when a user action results in a need to navigate to a different page of issues. This callback
	 * will be invoked with the cursor to paginate to, and whether last issue in the result set should be selected (for
	 * example, if the user is trying to navigate from the first issue in a page to the last issue in the previous page).
	 */
	onPageChange?: (cursor: string, shouldSelectLastIssue: boolean) => void;
};

/**
 * Render the table footer, composing together the default IssueCount and Pagination UI elements.
 */
const FooterRenderer = ({
	Footer,
	IssueCount,
	issues,
	onPageChange,
	issueCreateProjectKey,
}: Props) => {
	const data = useFragment(
		graphql`
			fragment footerRenderer_nativeIssueTable on JiraIssueConnection
			@argumentDefinitions(pageSize: { type: "Int" }) {
				...main_nativeIssueTable_IssueCountRenderer
				...paginationControls_nativeIssueTable @arguments(pageSize: $pageSize)
				totalIssueSearchResultCount
			}
		`,
		issues,
	);

	let start;
	let center;
	let end;
	// Only render issue count and pagination controls if onPageChange is provided
	if (onPageChange) {
		center = <IssueCountRenderer issues={data} IssueCount={IssueCount} />;
		end = (
			<PaginationControls
				issues={data}
				onPageChange={(after: string) => onPageChange(after, false)}
			/>
		);
	}

	if (ff('jsv-one-issue-table-issue-create')) {
		// eslint-disable-next-line react-hooks/rules-of-hooks
		const [isCreating, setIsCreating] = useState(false);
		// eslint-disable-next-line react-hooks/rules-of-hooks
		const cloudId = useCloudId();

		// eslint-disable-next-line react-hooks/rules-of-hooks
		const { entryPointActions, entryPointReferenceSubject } = useEntryPoint(
			ContextFieldsEntryPoint,
			{
				cloudId,
				projectKey: issueCreateProjectKey ?? '',
			},
		);
		// eslint-disable-next-line react-hooks/rules-of-hooks
		const buttonTrigger = useEntryPointButtonTrigger(entryPointActions);
		// eslint-disable-next-line react-hooks/rules-of-hooks
		const focusReturnRef = useRef<HTMLButtonElement>();
		// eslint-disable-next-line react-hooks/rules-of-hooks
		const buttonRef = useMergeRefs(focusReturnRef, buttonTrigger);

		// eslint-disable-next-line react-hooks/rules-of-hooks
		const onIssueCreateClose = useCallback((isKeyboard: boolean) => {
			setIsCreating(false);
			if (isKeyboard) {
				requestAnimationFrame(() => {
					focusReturnRef.current?.focus();
				});
			}
		}, []);

		if (issueCreateProjectKey) {
			start = <IssueCreateButton ref={buttonRef} onClick={() => setIsCreating(true)} />;
		}

		if (isCreating) {
			return (
				<IssueCreateFooter
					contextFieldsSubject={entryPointReferenceSubject}
					onClose={onIssueCreateClose}
				/>
			);
		}
	}

	const hasIssues = (data.totalIssueSearchResultCount ?? 0) > 0;
	const hasIssueConnectionControls = hasIssues && !!onPageChange;
	const hasControlsToDisplay =
		hasIssueConnectionControls || (ff('jsv-one-issue-table-issue-create') && start);

	// Hide the footer if we have no issues UNLESS the consumer has provided a custom footer component, in which case
	// the consumer becomes responsible for when the footer should be shown/hidden.
	if (!hasControlsToDisplay && !Footer) {
		return null;
	}

	const FooterToRender = Footer ?? DefaultFooter;
	return <FooterToRender start={start} center={center} end={end} />;
};

export default FooterRenderer;
